import {
  MICROSOFT_MARKETPLACE_ENDPOINTS,
  build_microsoft_marketplace_url,
} from '@/constants/system_management_endpoints.js';

const BUYER_AUTHENTICATED_STATUSES = [
  'BUYER_AUTHENTICATED',
  'AWAITING_INTERNAL_PROVISIONING',
  'PROVISIONING_IN_PROGRESS',
  'READY_FOR_ACTIVATION',
  'ACTIVATED',
];

const BUYER_NOT_LINKED_MESSAGE =
  'Your Microsoft sign-in was not linked to this Marketplace subscription. Please sign in with Microsoft again.';

export default {
  name: 'MicrosoftMarketplaceActivate',

  data: () => ({
    activation_reference: '',
    activation_details: null,
    buyer_context: null,
    loading_details: false,
    loading_context: false,
    submitting: false,
    error_message: '',
    validation_message: '',
    authorized_to_proceed: false,
  }),

  computed: {
    buyer_data() {
      return this.buyer_context?.buyer || {};
    },

    buyer_provisioning_status() {
      return String(
        this.buyer_context?.provisioning_status ||
        this.buyer_context?.subscription?.provisioning_status ||
        ''
      ).toUpperCase();
    },

    is_marketplace_verified() {
      return BUYER_AUTHENTICATED_STATUSES.includes(this.buyer_provisioning_status);
    },

    current_step() {
      return this.is_marketplace_verified ? 2 : 1;
    },

    offer_id() {
      return this.activation_details?.offer_id || this.activation_details?.offerId || '';
    },

    plan_id() {
      return this.activation_details?.plan_id || this.activation_details?.planId || '';
    },

    subscription_name() {
      return this.activation_details?.subscription_name || this.activation_details?.subscriptionName || '';
    },

    subscription_status() {
      return (
        this.activation_details?.subscription_status ||
        this.activation_details?.status ||
        'Pending fulfilment'
      );
    },

    buyer_name() {
      const buyer = this.buyer_data;
      return buyer.name || buyer.full_name || buyer.display_name || buyer.buyer_name || '';
    },

    buyer_email() {
      const buyer = this.buyer_data;
      return buyer.email || buyer.email_address || buyer.user_email || '';
    },

    buyer_organization() {
      const buyer = this.buyer_data;
      return buyer.organization || buyer.company || buyer.company_name || buyer.organization_name || '';
    },
  },

  async mounted() {
    this.activation_reference = this.get_activation_reference();

    if (!this.activation_reference) {
      this.error_message = 'Activation reference is missing.';
      return;
    }

    await this.fetch_activation_details();

    if (this.is_marketplace_verified || this.$route.query.marketplace_verified === '1') {
      await this.fetch_marketplace_onboarding_context();
    }
  },

  methods: {
    get_activation_reference() {
      const reference = this.$route.query.reference;
      if (typeof reference === 'string' && reference.trim()) {
        return reference.trim();
      }
      return localStorage.getItem('marketplace_activation_reference') || '';
    },

    async fetch_activation_details() {
      this.loading_details = true;
      this.error_message = '';

      try {
        const response = await fetch(this.build_reference_url(MICROSOFT_MARKETPLACE_ENDPOINTS.activation_details), {
          method: 'GET',
          credentials: 'include',
          headers: this.build_request_headers(),
        });
        const body = await this.parse_response_body(response);

        if (!response.ok || body.status === 'error') {
          this.error_message = this.get_response_message(body, 'Subscription details could not be loaded.');
          return;
        }

        this.activation_details = this.extract_activation_details(body);
      } catch {
        this.error_message = 'Subscription details could not be loaded.';
      } finally {
        this.loading_details = false;
      }
    },

    async fetch_marketplace_onboarding_context() {
      this.loading_context = true;
      this.error_message = '';
      this.buyer_context = null;

      try {
        const response = await fetch(this.build_reference_url(MICROSOFT_MARKETPLACE_ENDPOINTS.buyer_context), {
          method: 'GET',
          credentials: 'include',
          headers: this.build_request_headers(),
        });
        const body = await this.parse_response_body(response);

        if (!response.ok || body.status === 'error') {
          this.buyer_context = null;
          this.error_message = BUYER_NOT_LINKED_MESSAGE;
          return;
        }

        const payload = this.extract_marketplace_context(body);
        this.buyer_context = payload;

        if (!this.is_marketplace_verified) {
          this.error_message = BUYER_NOT_LINKED_MESSAGE;
        }
      } catch {
        this.buyer_context = null;
        this.error_message = BUYER_NOT_LINKED_MESSAGE;
      } finally {
        this.loading_context = false;
      }
    },

    build_reference_url(endpoint) {
      const reference = encodeURIComponent(this.activation_reference);
      return `${build_microsoft_marketplace_url(endpoint)}?reference=${reference}`;
    },

    build_request_headers() {
      return ({
        'Content-Type': 'application/json',
        'X-Page-URL': window.location.href,
      });
    },

    async parse_response_body(response) {
      return response.json().catch(() => ({}));
    },

    extract_activation_details(body) {
      return body.data?.activation_details || body.data?.subscription || body.data || body;
    },

    extract_marketplace_context(body) {
      return body.data?.buyer_context || body.data || body;
    },

    get_response_message(body, fallback_message) {
      return body.message || body.error || body.detail || fallback_message;
    },

    sign_in_with_microsoft() {
      localStorage.setItem('marketplace_activation_reference', this.activation_reference);
      const reference = encodeURIComponent(this.activation_reference);
      window.location =
        build_microsoft_marketplace_url(MICROSOFT_MARKETPLACE_ENDPOINTS.microsoft_login) +
        `?marketplace_reference=${reference}`;
    },

    sign_up_with_microsoft() {
      localStorage.setItem('marketplace_activation_reference', this.activation_reference);
      const reference = encodeURIComponent(this.activation_reference);
      window.location =
        build_microsoft_marketplace_url(MICROSOFT_MARKETPLACE_ENDPOINTS.microsoft_signup) +
        `?marketplace_reference=${reference}`;
    },

    async submit_setup_request() {
      this.validation_message = '';

      if (!this.activation_reference) {
        this.validation_message = 'Activation reference is missing.';
        return;
      }

      if (!this.is_marketplace_verified) {
        this.validation_message =
          'Please sign in with Microsoft and wait for your Marketplace account details to load before submitting this request.';
        return;
      }

      if (!this.authorized_to_proceed) {
        this.validation_message = 'Please confirm that you are authorised to request setup of this subscription.';
        return;
      }

      this.submitting = true;
      this.error_message = '';

      try {
        const response = await fetch(
          build_microsoft_marketplace_url(MICROSOFT_MARKETPLACE_ENDPOINTS.submit_setup_request),
          {
            method: 'POST',
            credentials: 'include',
            headers: this.build_request_headers(),
            body: JSON.stringify(this.build_setup_request_payload()),
          }
        );
        const body = await this.parse_response_body(response);

        if (!response.ok || body.status === 'error') {
          this.error_message = this.get_response_message(body, 'Setup request could not be submitted.');
          return;
        }

        localStorage.removeItem('marketplace_activation_reference');
        this.$router.push({
          name: 'MicrosoftMarketplaceSetupSubmitted',
          query: { reference: this.activation_reference },
        });
      } catch {
        this.error_message = 'Setup request could not be submitted.';
      } finally {
        this.submitting = false;
      }
    },

    build_setup_request_payload() {
      return ({
        activation_reference: this.activation_reference,
        authorized_to_proceed: true,
      });
    },

    safe_display_value(value) {
      return value || 'Not available';
    },

    organization_display_value() {
      return this.buyer_organization || 'Not available - our team will confirm this during setup.';
    },

    status_display_label() {
      return this.safe_display_value(this.subscription_status);
    },

    normalized_subscription_status() {
      return String(this.subscription_status || '').toLowerCase();
    },

    status_badge_class() {
      const status = this.normalized_subscription_status();

      if (status.includes('fail') || status.includes('error')) {
        return 'marketplace-status-danger';
      }

      if (status.includes('activated') || status.includes('subscribed')) {
        return 'marketplace-status-success';
      }

      return 'marketplace-status-pending';
    },

    is_step_active(step) {
      return this.current_step === step;
    },

    go_to_login() {
      this.$router.push({ name: 'Login' });
    },
  },
};
